import { useState } from "react";
import { Coffee, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Společný vzhled formulářů směny (odsouhlasený design dialogu „Odhlásit se z práce“):
// sekce v šedé kartě s oranžovou ikonou a přestávka jako jedno tlačítko s výběrem. Používá ho
// odhlášení (MyShiftBlock), Kontrola směny (ShiftReviewDialog) i ruční záznam
// (AttendanceAddDialogs) — ať se vzhled nerozjede.

export function Sec({ icon: Icon, title, action, children, testid }) {
  return (
    <section data-testid={testid}
      className="bg-secondary/60 border border-border rounded-[14px] p-4 space-y-3.5 min-w-0 [&_input]:bg-card [&_textarea]:bg-card">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading font-extrabold text-[12px] uppercase tracking-[0.07em]">
          {Icon && <Icon size={16} className="text-brand shrink-0" />}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

// „volitelné“ / „nepovinné“ za nadpisem sekce.
export const SecHint = ({ children }) => <span className="font-normal normal-case tracking-normal text-muted-foreground">{children}</span>;

const OPTIONS = [0, 15, 30, 45, 60];

// autoMinutes = kolik „auto“ u téhle směny skutečně odečte (30 min nad 6 h, jinak 0).
export const breakLabel = (v, withAuto, autoMinutes = 30) =>
  v === "" ? (withAuto ? `auto (${autoMinutes} min)` : "neuvedeno") : `${v} min`;

// Přestávka = jedno tlačítko „☕ Přestávka · 30 min ▾“. Klepnutí otevře malý
// výběr s velkými dlaždicemi (56 px — trefí se i v rukavicích): 0–60 min,
// „Jiná…“ a u schvalování „auto“. value "" = automaticky (nad 6 h −30 min).
export function BreakChips({ value, onChange, withAuto = false, autoMinutes = 30, informal = false, testid = "break", compact = false }) {
  const v = value === null || value === undefined ? "" : String(value);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState("");
  const isPreset = v === "" || OPTIONS.map(String).includes(v);
  const choose = (val) => { onChange(val); setOpen(false); setCustom(false); };
  const tile = (key, label, val) => (
    <button key={key} type="button" onClick={() => choose(val)} data-testid={`${testid}-chip-${key}`}
      className={`h-14 rounded-[16px] border text-[15px] font-extrabold tabular-nums transition-colors ${v === val && !custom ? "bg-brand border-brand text-white" : "bg-secondary/60 border-border hover:bg-secondary"}`}>{label}</button>
  );
  return (
    <>
      <button type="button" onClick={() => { setCustom(false); setDraft(isPreset ? "" : v); setOpen(true); }} data-testid={`${testid}-choice`}
        className={`w-full ${compact ? "h-11 px-3 text-[14px] justify-center gap-1" : "h-12 px-3.5 text-[15px] justify-between"} rounded-[14px] border border-border bg-card flex items-center font-bold hover:bg-secondary transition-colors`}>
        {compact ? <><Coffee size={15} className="text-muted-foreground shrink-0" /><span className="text-brand whitespace-nowrap tabular-nums">{breakLabel(v, withAuto, autoMinutes)}</span><ChevronDown size={15} className="text-brand shrink-0" /></>
          : <><span className="flex items-center gap-2"><Coffee size={17} className="text-muted-foreground" /> Přestávka</span>
            <span className="flex items-center gap-1 text-brand whitespace-nowrap tabular-nums">{breakLabel(v, withAuto, autoMinutes)} <ChevronDown size={16} /></span></>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent sheet className="max-w-sm p-4 gap-3 max-md:pt-8" data-testid={`${testid}-picker`} onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogTitle className="text-center text-[15px] font-bold pr-8 md:pr-0">{informal ? "Jak dlouhá byla přestávka?" : "Přestávka"}</DialogTitle>
          <div className="grid grid-cols-3 gap-2" data-testid={`${testid}-chips`}>
            {withAuto && tile("auto", `auto · ${autoMinutes} min`, "")}
            {OPTIONS.map((m) => tile(m, `${m} min`, String(m)))}
            <button type="button" onClick={() => setCustom(true)} data-testid={`${testid}-chip-custom`}
              className={`h-14 rounded-[16px] border text-[15px] font-extrabold transition-colors ${custom || !isPreset ? "bg-brand/10 border-brand text-brand" : "bg-secondary/60 border-border hover:bg-secondary"}`}>{!isPreset && !custom ? `${v} min` : "Jiná…"}</button>
          </div>
          {custom && (
            <div className="flex gap-2">
              <Input type="number" inputMode="numeric" min={0} max={720} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
                placeholder="minut" className="h-12 w-full min-w-0 text-[16px]" data-testid={`${testid}-input`} aria-label="Přestávka v minutách" />
              <Button type="button" onClick={() => choose(draft === "" ? "0" : String(Math.max(0, Math.min(720, Number(draft) || 0))))} className="h-12 font-bold px-5" data-testid={`${testid}-custom-ok`}>Hotovo</Button>
            </div>
          )}
          <p className="text-[12px] text-muted-foreground text-center">{informal ? "Když nic nevybereš" : withAuto ? "Při „auto“" : "Když nic nevyberete"}, u směny nad 6 hodin se automaticky odečte 30 min.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
